import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { teamService } from '../../services/teamService';
import { emailService } from '../../services/emailService';
import LoginModal from '../administrative-dashboard/components/LoginModal';
import ScheduleModal from './components/ScheduleModal';
import MemberModal from './components/MemberModal';
import AutoAssignModal from './components/AutoAssignModal';

const TeamPlanningScheduling = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');
  const [loading, setLoading] = useState(true);
  const { variants, transition } = usePageTransition();
  
  // Team Members State
  const [members, setMembers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('all');
  
  // Schedules State
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // list, calendar
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Modals
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        setShowLoginModal(true);
      } else {
        setShowLoginModal(false);
        loadData();
      }
    }
  }, [user, authLoading]);

  useEffect(() => {
    applyFilters();
  }, [schedules, selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersData, schedulesData] = await Promise.all([
        teamService?.getAllMembers(),
        teamService?.getAllSchedules(),
      ]);
      setMembers(membersData);
      setSchedules(schedulesData);
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = schedules;

    if (selectedStatus !== 'all') {
      filtered = filtered?.filter(s => s?.status === selectedStatus);
    }

    setFilteredSchedules(filtered);
  };

  const handleAutoAssign = async (assignmentData) => {
    try {
      const bestMember = await teamService?.findBestMemberForAssignment(assignmentData);
      
      if (bestMember) {
        const newSchedule = await teamService?.createSchedule({
          teamMemberId: bestMember?.id,
          assignmentTitle: assignmentData?.title,
          description: assignmentData?.description,
          serviceType: assignmentData?.serviceType,
          clientName: assignmentData?.clientName,
          clientPhone: assignmentData?.clientPhone,
          location: assignmentData?.location,
          startTime: assignmentData?.startTime,
          endTime: assignmentData?.endTime,
          priority: assignmentData?.priority,
          requiredMaterials: assignmentData?.requiredMaterials || [],
          status: 'assigned',
        });

        // Send email notification
        if (bestMember?.email) {
          await emailService?.sendScheduleAssignmentEmail(
            newSchedule,
            bestMember?.email,
            bestMember?.fullName
          );
        }

        alert(`Mission assignée automatiquement à ${bestMember?.fullName}`);
        loadData();
        setShowAutoAssignModal(false);
      } else {
        alert('Aucun membre disponible pour cette mission');
      }
    } catch (error) {
      console.error('Error auto-assigning:', error);
      alert('Erreur lors de l\'assignation automatique');
    }
  };

  const handleScheduleSubmit = async (formData) => {
    try {
      if (editingSchedule) {
        await teamService?.updateSchedule(editingSchedule?.id, formData);
      } else {
        await teamService?.createSchedule(formData);
      }
      setShowScheduleModal(false);
      setEditingSchedule(null);
      loadData();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Erreur lors de la sauvegarde de la mission');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette mission ?')) return;
    try {
      await teamService?.deleteSchedule(scheduleId);
      loadData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleMemberSubmit = async (formData) => {
    try {
      if (editingMember) {
        await teamService?.updateMember(editingMember?.id, formData);
      } else {
        await teamService?.createMember(formData);
      }
      setShowMemberModal(false);
      setEditingMember(null);
      loadData();
    } catch (error) {
      console.error('Error saving member:', error);
      alert('Erreur lors de la sauvegarde du membre');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;
    try {
      await teamService?.deleteMember(memberId);
      loadData();
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const roleOptions = [
    { value: 'all', label: 'Tous les rôles' },
    { value: 'technician', label: 'Techniciens' },
    { value: 'driver', label: 'Chauffeurs' },
    { value: 'manager', label: 'Managers' },
  ];

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'pending', label: 'En attente' },
    { value: 'assigned', label: 'Assigné' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'completed', label: 'Terminé' },
    { value: 'cancelled', label: 'Annulé' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'var(--color-warning)',
      assigned: 'var(--color-trust)',
      in_progress: 'var(--color-primary)',
      completed: 'var(--color-success)',
      cancelled: 'var(--color-error)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      assigned: 'Assigné',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
    };
    return labels?.[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'var(--color-muted-foreground)',
      normal: 'var(--color-trust)',
      high: 'var(--color-warning)',
      urgent: 'var(--color-error)',
    };
    return colors?.[priority] || 'var(--color-muted-foreground)';
  };

  const getRoleLabel = (role) => {
    const labels = {
      technician: 'Technicien',
      driver: 'Chauffeur',
      manager: 'Manager',
      admin: 'Administrateur',
    };
    return labels?.[role] || role;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <motion.div 
        className="flex-1 container mx-auto px-4 py-8 pt-24"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Planification & Gestion d'Équipe
              </h1>
              <p className="text-muted-foreground">
                Calendrier des techniciens/chauffeurs et assignation automatique
              </p>
            </div>
            <Button onClick={() => navigate('/administrative-dashboard')}>
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Retour au Dashboard
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Membres d'Équipe</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground">{members?.length}</p>
                </div>
                <Icon name="Users" size={40} className="text-primary opacity-20" />
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Missions Actives</p>
                  <p className="text-2xl md:text-3xl font-bold text-primary">
                    {schedules?.filter(s => s?.status === 'in_progress')?.length}
                  </p>
                </div>
                <Icon name="Activity" size={40} className="text-primary opacity-20" />
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">En Attente</p>
                  <p className="text-2xl md:text-3xl font-bold text-warning">
                    {schedules?.filter(s => s?.status === 'pending')?.length}
                  </p>
                </div>
                <Icon name="Clock" size={40} className="text-warning opacity-20" />
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Terminées Aujourd'hui</p>
                  <p className="text-2xl md:text-3xl font-bold text-success">
                    {schedules?.filter(s => 
                      s?.status === 'completed' && 
                      new Date(s?.completedAt)?.toDateString() === new Date()?.toDateString()
                    )?.length}
                  </p>
                </div>
                <Icon name="CheckCircle" size={40} className="text-success opacity-20" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-card rounded-xl shadow-premium border border-border">
          <div className="border-b border-border">
            <div className="flex gap-2 md:gap-4 p-3 md:p-4 overflow-x-auto">
              {[
                { id: 'calendar', label: 'Calendrier', icon: 'Calendar' },
                { id: 'team', label: 'Équipe', icon: 'Users' },
                { id: 'auto-assign', label: 'Assignation Auto', icon: 'Zap' },
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 text-sm rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <Select
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    className="w-full md:w-64"
                  />
                  <Button onClick={() => { setEditingSchedule(null); setShowScheduleModal(true); }}>
                    <Icon name="Plus" size={20} className="mr-2" />
                    Nouvelle Mission
                  </Button>
                </div>

                <div className="space-y-4">
                  {filteredSchedules?.map((schedule) => {
                    const member = members?.find(m => m?.id === schedule?.teamMemberId);
                    return (
                      <div
                        key={schedule?.id}
                        className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                                {schedule?.assignmentTitle}
                              </h3>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold"
                                style={{
                                  backgroundColor: `${getStatusColor(schedule?.status)}15`,
                                  color: getStatusColor(schedule?.status),
                                }}
                              >
                                {getStatusLabel(schedule?.status)}
                              </span>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold"
                                style={{
                                  backgroundColor: `${getPriorityColor(schedule?.priority)}15`,
                                  color: getPriorityColor(schedule?.priority),
                                }}
                              >
                                {schedule?.priority?.toUpperCase()}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <p className="text-muted-foreground">Membre</p>
                                <p className="font-medium text-foreground">{member?.fullName}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Client</p>
                                <p className="font-medium text-foreground">{schedule?.clientName}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Localisation</p>
                                <p className="font-medium text-foreground">{schedule?.location}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Début</p>
                                <p className="font-medium text-foreground">
                                  {new Date(schedule?.startTime)?.toLocaleString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              </div>
                            </div>
                            {schedule?.description && (
                              <p className="text-sm text-muted-foreground">{schedule?.description}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setEditingSchedule(schedule); setShowScheduleModal(true); }}
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSchedule(schedule?.id)}
                              className="text-error hover:bg-error/10"
                            >
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Team Tab */}
            {activeTab === 'team' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                  <Select
                    options={roleOptions}
                    value={selectedRole}
                    onChange={setSelectedRole}
                    className="w-full sm:w-64"
                  />
                  <Button onClick={() => { setEditingMember(null); setShowMemberModal(true); }}>
                    <Icon name="Plus" size={20} className="mr-2" />
                    Nouveau Membre
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {members
                    ?.filter(m => selectedRole === 'all' || m?.role === selectedRole)
                    ?.map((member) => (
                      <div
                        key={member?.id}
                        className="bg-muted/50 rounded-xl p-6 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                              <Icon name="User" size={24} className="text-primary" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-foreground">{member?.fullName}</h3>
                              <p className="text-sm text-muted-foreground">{getRoleLabel(member?.role)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setEditingMember(member); setShowMemberModal(true); }}
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteMember(member?.id)}
                              className="text-error hover:bg-error/10"
                            >
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Icon name="Mail" size={16} className="text-muted-foreground" />
                            <span className="text-foreground">{member?.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="Phone" size={16} className="text-muted-foreground" />
                            <span className="text-foreground">{member?.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="MapPin" size={16} className="text-muted-foreground" />
                            <span className="text-foreground">{member?.location}</span>
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                            <div>
                              <p className="text-xs text-muted-foreground">Performance</p>
                              <p className="font-semibold text-foreground">{member?.performanceScore}/5</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Missions</p>
                              <p className="font-semibold text-foreground">
                                {member?.completedAssignments}/{member?.totalAssignments}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Auto-Assign Tab */}
            {activeTab === 'auto-assign' && (
              <div>
                <div className="max-w-2xl mx-auto">
                  <div className="text-center mb-8">
                    <Icon name="Zap" size={64} className="text-primary mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      Assignation Automatique
                    </h2>
                    <p className="text-muted-foreground">
                      Notre algorithme intelligent trouve le meilleur membre d'équipe pour chaque mission
                      en fonction des compétences, localisation, disponibilité et performance.
                    </p>
                  </div>

                  <div className="bg-muted/50 rounded-xl p-6 mb-6">
                    <h3 className="font-semibold text-foreground mb-4">Critères d'assignation</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        Disponibilité au moment de la mission
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        Compétences et spécialisations requises
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        Proximité géographique
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        Score de performance
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        Charge de travail actuelle
                      </li>
                    </ul>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => setShowAutoAssignModal(true)}
                  >
                    <Icon name="Zap" size={20} className="mr-2" />
                    Créer une Mission avec Assignation Auto
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
      <Footer />
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => navigate('/')}
          onLogin={() => setShowLoginModal(false)}
        />
      )}

      {showScheduleModal && (
        <ScheduleModal
          schedule={editingSchedule}
          members={members}
          onClose={() => { setShowScheduleModal(false); setEditingSchedule(null); }}
          onSubmit={handleScheduleSubmit}
        />
      )}

      {showMemberModal && (
        <MemberModal
          member={editingMember}
          onClose={() => { setShowMemberModal(false); setEditingMember(null); }}
          onSubmit={handleMemberSubmit}
        />
      )}

      {showAutoAssignModal && (
        <AutoAssignModal
          onClose={() => setShowAutoAssignModal(false)}
          onSubmit={handleAutoAssign}
        />
      )}
    </div>
  );
};

export default TeamPlanningScheduling;